import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Button,
    IconButton,
    Divider,
    TextField,
    CircularProgress,
    Alert,
    Snackbar
} from '@mui/material';
import {
    Close,
    Person,
    Lock,
    Phone,
    Badge,
    Visibility,
    VisibilityOff,
    Edit,
    Save,
    Cancel,
    Settings,
    Language,
    RestartAlt
} from '@mui/icons-material';
import axios from 'axios';
import ChangePasswordDialog from './ChangePasswordDialog';

interface UserProfileProps {
    open: boolean;
    onClose: () => void;
    username: string;
    pharmacyNum: string | null;
    forceCheck?: boolean;
    onConfirm?: () => void;
}

interface UserData {
    fullName: string;
    userphone: string;
    iin: string;
}

const UserProfile: React.FC<UserProfileProps> = ({
    open,
    onClose,
    username,
    pharmacyNum,
    forceCheck = false,
    onConfirm
}) => {
    const [userData, setUserData] = useState<UserData>({
        fullName: '',
        userphone: '',
        iin: ''
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState<UserData>({
        fullName: '',
        userphone: '',
        iin: ''
    });
    const [saving, setSaving] = useState(false);
    const [showIIN, setShowIIN] = useState(false);
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [canClose, setCanClose] = useState(!forceCheck);
    const [countdown, setCountdown] = useState(forceCheck ? 10 : 0);

    const fetchUserData = async () => {
        if (!username) return;

        setLoading(true);
        setError(null);

        try {
            const response = await axios.get('https://tbl.zerdegroup.kz/api/user-stitstic-info', {
                params: { username }
            });

            if (response.data.success) {
                setUserData({
                    fullName: response.data.fullName || '',
                    userphone: response.data.userphone || '',
                    iin: response.data.iin || ''
                });
            } else {
                setError('Не удалось загрузить данные пользователя');
            }
        } catch (err) {
            console.error('Error fetching user data:', err);
            setError('Ошибка при загрузке данных');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchUserData();
            setShowIIN(false);
            setIsEditing(false);

            // Reset timer for forceCheck mode
            if (forceCheck) {
                setCanClose(false);
                setCountdown(10);
            } else {
                setCanClose(true);
                setCountdown(0);
            }
        }
    }, [open, username, forceCheck]);

    // Countdown timer for forceCheck mode
    useEffect(() => {
        if (!open || !forceCheck || countdown <= 0) return;

        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    setCanClose(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [open, forceCheck, countdown]);

    const maskIIN = (iin: string): string => {
        if (!iin || iin.length < 6) return iin;
        // First 2 digits + *** + last 4 digits
        return `${iin.substring(0, 2)}***${iin.substring(iin.length - 4)}`;
    };

    const formatPhone = (phone: string): string => {
        if (!phone) return 'Не указан';
        // Format as +7 (XXX) XXX-XX-XX
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 11) {
            return `+${cleaned[0]} (${cleaned.substring(1, 4)}) ${cleaned.substring(4, 7)}-${cleaned.substring(7, 9)}-${cleaned.substring(9, 11)}`;
        }
        return phone;
    };

    const handleStartEdit = () => {
        setEditData({ ...userData });
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditData({ fullName: '', userphone: '', iin: '' });
    };

    const handleSaveEdit = async () => {
        setSaving(true);

        try {
            const response = await axios.post('https://tbl.zerdegroup.kz/api/user-data-update', {
                username,
                fullName: editData.fullName,
                userphone: editData.userphone,
                iin: editData.iin
            });

            if (response.data.success) {
                setUserData({ ...editData });
                setIsEditing(false);
                setSnackbar({
                    open: true,
                    message: 'Данные успешно сохранены',
                    severity: 'success'
                });
            } else {
                setSnackbar({
                    open: true,
                    message: response.data.message || 'Ошибка при сохранении',
                    severity: 'error'
                });
            }
        } catch (err) {
            console.error('Error saving user data:', err);
            setSnackbar({
                open: true,
                message: 'Ошибка сервера при сохранении',
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleResetTimesheet = () => {
        // Delete the cookie by setting expired date
        document.cookie = 'timesheet_folder_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        // Also remove from localStorage if it exists there
        localStorage.removeItem('timesheet_folder_id');

        setSnackbar({
            open: true,
            message: 'Табель РБ сброшен. Перезагрузите страницу для применения изменений.',
            severity: 'success'
        });
    };

    const SectionTitle: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2,
            color: '#18181b' // Zinc-900
        }}>
            {React.cloneElement(icon as React.ReactElement, { sx: { color: '#000000' } })}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}>
                {title}
            </Typography>
        </Box>
    );

    const InfoRow: React.FC<{ label: string; value: string; icon?: React.ReactNode }> = ({ label, value, icon }) => (
        <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1.5,
            borderBottom: '1px solid #f4f4f5' // Zinc-100
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#71717a' }}>
                {icon && React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 18, color: '#a1a1aa' } })}
                <Typography variant="body2" sx={{ fontWeight: 400 }}>{label}</Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#09090b', textAlign: 'right' }}>
                {value}
            </Typography>
        </Box>
    );

    const handleClose = () => {
        if (forceCheck && !canClose) return;
        onClose();
    };

    const handleConfirm = () => {
        if (!canClose) return;
        if (onConfirm) onConfirm();
        onClose();
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                disableEscapeKeyDown={forceCheck && !canClose}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        background: 'white',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'white',
                    color: '#09090b',
                    p: 3,
                    pb: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Person sx={{ color: '#000000' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.02em' }}>
                            {forceCheck ? 'Проверьте данные' : 'Личный кабинет'}
                        </Typography>
                    </Box>
                    {(!forceCheck || canClose) && (
                        <IconButton onClick={handleClose} sx={{ color: '#a1a1aa', '&:hover': { color: '#09090b', bgcolor: '#f4f4f5' } }}>
                            <Close />
                        </IconButton>
                    )}
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress sx={{ color: '#000000' }} />
                        </Box>
                    ) : error ? (
                        <Box sx={{ p: 3 }}>
                            <Alert severity="error" sx={{ bgcolor: '#fef2f2', color: '#991b1b', borderRadius: '8px' }}>
                                {error}
                            </Alert>
                        </Box>
                    ) : (
                        <Box sx={{ p: 3, pt: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>

                            {/* Section 1: Site Data */}
                            <Box>
                                <SectionTitle icon={<Language fontSize="small" />} title="Данные сайта" />
                                <Box sx={{
                                    border: '1px solid #e4e4e7',
                                    borderRadius: '12px',
                                    overflow: 'hidden'
                                }}>
                                    <Box sx={{ px: 2 }}>
                                        <InfoRow label="Логин" value={username} icon={<Person />} />
                                        <InfoRow
                                            label="Номер аптеки"
                                            value={pharmacyNum ? `Аптека №${pharmacyNum}` : 'Не указан'}
                                            icon={<Badge />}
                                        />
                                    </Box>
                                    {!forceCheck && (
                                        <Box sx={{ p: 1.5, bgcolor: '#fafafa', borderTop: '1px solid #f4f4f5' }}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<Lock />}
                                                onClick={() => setChangePasswordOpen(true)}
                                                fullWidth
                                                sx={{
                                                    color: '#09090b',
                                                    borderColor: '#e4e4e7',
                                                    bgcolor: 'white',
                                                    textTransform: 'none',
                                                    fontWeight: 500,
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                    '&:hover': {
                                                        borderColor: '#d4d4d8',
                                                        bgcolor: '#f4f4f5'
                                                    }
                                                }}
                                            >
                                                Сменить пароль
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            {/* Section 2: Personal Data */}
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <SectionTitle icon={<Badge fontSize="small" />} title="Личные данные" />
                                    {!isEditing ? (
                                        <Button
                                            size="small"
                                            startIcon={<Edit sx={{ fontSize: 18 }} />}
                                            onClick={handleStartEdit}
                                            sx={{
                                                color: '#52525b',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                fontSize: '0.875rem',
                                                '&:hover': { color: '#09090b', bgcolor: 'transparent' }
                                            }}
                                        >
                                            Редактировать
                                        </Button>
                                    ) : (
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                                size="small"
                                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: 18 }} />}
                                                onClick={handleSaveEdit}
                                                disabled={saving}
                                                sx={{
                                                    color: '#09090b',
                                                    fontWeight: 600,
                                                    textTransform: 'none',
                                                    bgcolor: '#f4f4f5',
                                                    px: 2,
                                                    borderRadius: '6px',
                                                    '&:hover': { bgcolor: '#e4e4e7' }
                                                }}
                                            >
                                                Сохранить
                                            </Button>
                                            <Button
                                                size="small"
                                                startIcon={<Cancel sx={{ fontSize: 18 }} />}
                                                onClick={handleCancelEdit}
                                                disabled={saving}
                                                sx={{ color: '#71717a', textTransform: 'none' }}
                                            >
                                                Отмена
                                            </Button>
                                        </Box>
                                    )}
                                </Box>

                                <Box sx={{
                                    border: '1px solid #e4e4e7',
                                    borderRadius: '12px',
                                    overflow: 'hidden'
                                }}>
                                    {isEditing ? (
                                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <TextField
                                                label="ФИО заведующего"
                                                value={editData.fullName}
                                                onChange={(e) => setEditData({ ...editData, fullName: e.target.value })}
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                InputProps={{ sx: { borderRadius: '8px' } }}
                                            />
                                            <TextField
                                                label="Номер телефона"
                                                value={editData.userphone}
                                                onChange={(e) => setEditData({ ...editData, userphone: e.target.value })}
                                                fullWidth
                                                size="small"
                                                placeholder="+7 (XXX) XXX-XX-XX"
                                                InputProps={{ sx: { borderRadius: '8px' } }}
                                            />
                                            <TextField
                                                label="ИИН"
                                                value={editData.iin}
                                                onChange={(e) => setEditData({ ...editData, iin: e.target.value })}
                                                fullWidth
                                                size="small"
                                                inputProps={{ maxLength: 12 }}
                                                InputProps={{ sx: { borderRadius: '8px' } }}
                                            />
                                        </Box>
                                    ) : (
                                        <Box sx={{ px: 2 }}>
                                            <InfoRow
                                                label="ФИО заведующего"
                                                value={userData.fullName || 'Не указано'}
                                                icon={<Person />}
                                            />
                                            <InfoRow
                                                label="Номер телефона"
                                                value={formatPhone(userData.userphone)}
                                                icon={<Phone />}
                                            />
                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                py: 1.5,
                                                // borderBottom: '1px solid #f4f4f5'
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#71717a' }}>
                                                    <Badge sx={{ fontSize: 18, color: '#a1a1aa' }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 400 }}>ИИН</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace', color: '#09090b' }}>
                                                        {userData.iin ? (showIIN ? userData.iin : maskIIN(userData.iin)) : 'Не указан'}
                                                    </Typography>
                                                    {userData.iin && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setShowIIN(!showIIN)}
                                                            sx={{ color: '#71717a', p: 0.5, ml: 1 }}
                                                        >
                                                            {showIIN ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            {/* Section 3: Settings - hidden in forceCheck mode */}
                            {!forceCheck && (
                                <Box>
                                    <SectionTitle icon={<Settings fontSize="small" />} title="Настройки" />
                                    <Box sx={{
                                        border: '1px solid #e4e4e7',
                                        borderRadius: '12px',
                                        p: 2,
                                        bgcolor: 'white'
                                    }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#09090b', mb: 0.5 }}>
                                                    Сбросить табель РБ
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#71717a' }}>
                                                    Удаляет привязку к папке табеля
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<RestartAlt />}
                                                onClick={handleResetTimesheet}
                                                sx={{
                                                    borderColor: '#e4e4e7',
                                                    color: '#ef4444',
                                                    textTransform: 'none',
                                                    borderRadius: '6px',
                                                    '&:hover': {
                                                        borderColor: '#ef4444',
                                                        bgcolor: '#fef2f2'
                                                    }
                                                }}
                                            >
                                                Сбросить
                                            </Button>
                                        </Box>
                                    </Box>
                                </Box>
                            )}

                            {/* Confirm button for forceCheck mode */}
                            {forceCheck && (
                                <Box sx={{ mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        disabled={!canClose}
                                        onClick={handleConfirm}
                                        sx={{
                                            bgcolor: canClose ? '#09090b' : '#a1a1aa',
                                            color: 'white',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            py: 1.5,
                                            borderRadius: '8px',
                                            '&:hover': {
                                                bgcolor: canClose ? '#27272a' : '#a1a1aa'
                                            },
                                            '&:disabled': {
                                                bgcolor: '#a1a1aa',
                                                color: 'white'
                                            }
                                        }}
                                    >
                                        {canClose ? 'Данные верны' : `Подождите ${countdown} сек...`}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <ChangePasswordDialog
                open={changePasswordOpen}
                onClose={() => setChangePasswordOpen(false)}
                username={username}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    variant="filled"
                    sx={{
                        borderRadius: '8px',
                        bgcolor: snackbar.severity === 'error' ? '#ef4444' : '#10b981',
                        color: 'white'
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default UserProfile;
